import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { BookOpen, HelpCircle, Loader2, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react'
import { KbTab, fmtDate } from './AdminSuporte'
import {
  fetchUnansweredQuestions, resolveUnansweredQuestion,
  type UnansweredQuestion, type PagedResponse,
} from '@/lib/supportAdminApi'

// ─── Unanswered Tab ───────────────────────────────────────────────────────────

function UnansweredTab() {
  const [data, setData] = useState<PagedResponse<UnansweredQuestion> | null>(null)
  const [loading, setLoading] = useState(true)
  const [filterResolved, setFilterResolved] = useState<'pending' | 'resolved' | 'all'>('pending')
  const [page, setPage] = useState(1)
  const [resolveDialog, setResolveDialog] = useState<UnansweredQuestion | null>(null)
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const is_resolved =
        filterResolved === 'all' ? undefined : filterResolved === 'resolved'
      const res = await fetchUnansweredQuestions({ is_resolved, page })
      setData(res)
    } catch { /* ignore */ }
    finally { setLoading(false) }
  }, [filterResolved, page])

  useEffect(() => { load() }, [load])

  const openResolve = (q: UnansweredQuestion) => {
    setResolveDialog(q)
    setNotes(q.notes ?? '')
  }

  const handleResolve = async () => {
    if (!resolveDialog) return
    setSaving(true)
    try {
      await resolveUnansweredQuestion(resolveDialog.id, notes || undefined)
      setResolveDialog(null)
      load()
    } catch { /* ignore */ }
    finally { setSaving(false) }
  }

  const totalPages = data ? Math.ceil(data.total / data.per_page) : 1

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <select
          value={filterResolved}
          onChange={(e) => {
            setFilterResolved(e.target.value as 'pending' | 'resolved' | 'all')
            setPage(1)
          }}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="pending">Pendentes</option>
          <option value="resolved">Resolvidas</option>
          <option value="all">Todas</option>
        </select>
        <Button size="sm" variant="outline" onClick={load}>Atualizar</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Pergunta</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    Nenhuma pergunta sem resposta.
                  </TableCell>
                </TableRow>
              )}
              {data?.items.map((q) => (
                <TableRow key={q.id}>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {fmtDate(q.created_at)}
                  </TableCell>
                  <TableCell className="text-sm max-w-xs">
                    <p className="truncate">{q.question}</p>
                    {q.notes && (
                      <p className="text-xs text-muted-foreground mt-0.5 italic truncate">
                        Nota: {q.notes}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>
                    {q.is_resolved ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                        <CheckCircle2 className="h-3 w-3" />Resolvida
                      </span>
                    ) : (
                      <span className="text-xs text-amber-600">Pendente</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {!q.is_resolved && (
                      <Button
                        size="sm" variant="outline" className="h-7 text-xs"
                        onClick={() => openResolve(q)}
                      >
                        Resolver
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>{data?.total} perguntas</span>
              <div className="flex items-center gap-2">
                <Button
                  size="icon" variant="outline" className="h-7 w-7"
                  disabled={page === 1} onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span>Página {page} de {totalPages}</span>
                <Button
                  size="icon" variant="outline" className="h-7 w-7"
                  disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Resolve dialog */}
      <Dialog open={!!resolveDialog} onOpenChange={(open) => !open && setResolveDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Resolver pergunta</DialogTitle>
            <DialogDescription className="line-clamp-3">
              {resolveDialog?.question}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1 py-2">
            <Label>Notas (opcional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Descreva como a pergunta foi resolvida ou adicione contexto..."
              rows={3}
              className="text-sm resize-none"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialog(null)}>Cancelar</Button>
            <Button onClick={handleResolve} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Marcar como resolvida
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdminConhecimento() {
  const { user, loading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (loading) return
    if (!user) navigate('/auth', { replace: true })
  }, [user, loading, navigate])

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  )

  if (!user) return null

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Base de Conhecimento</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Artigos, arquivos e perguntas sem resposta do chat.
        </p>
      </div>

      <Tabs defaultValue="kb">
        <TabsList>
          <TabsTrigger value="kb" className="gap-1.5">
            <BookOpen className="h-3.5 w-3.5" />Artigos & Arquivos
          </TabsTrigger>
          <TabsTrigger value="unanswered" className="gap-1.5">
            <HelpCircle className="h-3.5 w-3.5" />Não respondidas
          </TabsTrigger>
        </TabsList>

        <Card className="mt-4 p-6">
          <TabsContent value="kb" className="mt-0">
            <KbTab />
          </TabsContent>
          <TabsContent value="unanswered" className="mt-0">
            <UnansweredTab />
          </TabsContent>
        </Card>
      </Tabs>
    </div>
  )
}
